import { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useSocketContext } from "@/context/socket.context/socket.context";
import { useAppSelector } from "@/hooks/redux-hooks";
import { TJoinedClient } from "@/models/call.center.model";
import { TWaitingRoomEntry } from "@/hooks/call-center/useWaitingRoom";
import { getAgentAvailable } from "@/lib/agentAvailability";
import {
    CUSTOM_EVENT_USER_JOINED,
    SOCKET_EVENT_WAITING_ROOM_JOIN,
    SESSION_STATUS_WAITING,
    CUSTOM_EVENT_INTRO_VIDEO,
} from "@icebergeradmin/common";
import { getCallLeadUrl, getJoinMeetingUrl } from "@/utils/paths";

const LEAD_ROLE = 0;
const AUTO_HIDE_MS = 10_000; // 10 seconds
const JOIN_MEETING_PATH_PREFIX = "/call-center/join-meeting/";

const isOnJoinMeetingScreen = () =>
    window.location.pathname.startsWith(JOIN_MEETING_PATH_PREFIX);

const showPushNotification = (lead: TJoinedClient) => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;

    const notification = new Notification("A new lead joined the lobby", {
        body: `${lead.name} — Hot Lead`,
        icon: "/favicon.ico",
        tag: lead.sessionName,
    });

    // Route by device — mobile leads are handled via the Twilio "Call Lead"
    // flow, desktop leads via the Zoom join-meeting flow.
    const targetUrl =
        lead.deviceType === "mobile"
            ? getCallLeadUrl(lead.sessionName)
            : getJoinMeetingUrl(lead.sessionName);

    notification.onclick = () => {
        window.focus();
        window.location.href = targetUrl;
        notification.close();
    };
};

export const useNewLeadNotifier = () => {
    const socket = useSocketContext();
    const { enqueueSnackbar } = useSnackbar();
    const user = useAppSelector((s) => s.auth.user);

    useEffect(() => {
        const notify = (lead: TJoinedClient) => {
            console.log("notifying new lead", lead);
            if (!getAgentAvailable()) return;
            // Don't notify when the agent is already on the join-meeting screen
            if (isOnJoinMeetingScreen()) return;

            if (document.hidden) {
                console.log("document is hidden, showing push notification");
                showPushNotification(lead);
            } else {
                console.log("document is visible, playing audio");
                const audio = new Audio("/sounds/new-lead-joined.wav");
                audio.volume = 0.5;
                audio.play().catch(() => {
                    console.log("audio play failed");
                    // Browser may block autoplay until user has interacted with the page
                });

                enqueueSnackbar("", {
                    variant: "newLeadNotification",
                    lead,
                    autoHideDuration: AUTO_HIDE_MS,
                    anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                    },
                });
            }
        };

        const handleUserJoined = (client: TJoinedClient) => {
            if (client.role !== LEAD_ROLE) return;
            notify(client);
        };

        const handleWaitingRoomJoin = (entry: TWaitingRoomEntry) => {
            const personalRoomSessionName =
                user?.companyUniqueName && user?.personalRoomId
                    ? `${user.companyUniqueName}-${user.personalRoomId}`
                    : "";

            if (!personalRoomSessionName) return;

            const lead: TJoinedClient = {
                email: entry.email,
                name: entry.name,
                role: 0,
                leadId: 0,
                sessionId: "",
                sessionName: personalRoomSessionName,
                sessionStatus: SESSION_STATUS_WAITING,
                startDate: new Date().toISOString(),
                // Carry the device metadata so `notify` / `showPushNotification`
                // can route the click target correctly (Call Lead for mobile,
                // Join Meeting for desktop).
                deviceType: entry.deviceType,
                phoneNumber: entry.phoneNumber,
            };

            notify(lead);
        };

        socket.on(CUSTOM_EVENT_USER_JOINED, handleUserJoined);
        socket.on(CUSTOM_EVENT_INTRO_VIDEO, handleUserJoined);
        socket.on(SOCKET_EVENT_WAITING_ROOM_JOIN, handleWaitingRoomJoin);

        return () => {
            socket.off(CUSTOM_EVENT_USER_JOINED, handleUserJoined);
            socket.off(CUSTOM_EVENT_INTRO_VIDEO, handleUserJoined);
            socket.off(SOCKET_EVENT_WAITING_ROOM_JOIN, handleWaitingRoomJoin);
        };
    }, [socket, enqueueSnackbar, user?.companyUniqueName, user?.personalRoomId]);
};
